import { openMetadataApiClient } from '@/shared/api/core/axios-instance';
import { getEntityEndpoint } from '@/shared/api/openmetadata';

export type DomainItem = {
  id: string;
  domainType: string;
  name: string;
  fullyQualifiedName: string;
  displayName: string;
  description: string;
  style: Record<string, unknown>;
  version: number;
  updatedAt: number;
  updatedBy: string;
  href: string;
  parent?: {
    id: string;
    type: string;
    name: string;
    fullyQualifiedName: string;
    description: string;
    displayName: string;
    href: string;
  };
};

export type DomainsResponse = {
  data: DomainItem[];
  paging: {
    total: number;
  };
};

export type DomainTreeNode = {
  key: string;
  value: string;
  title: string;
  children?: DomainTreeNode[];
  description?: string;
  data: DomainItem;
};

export const fetchDomains = async (): Promise<DomainsResponse> => {
  try {
    const response = await openMetadataApiClient.get<DomainsResponse>('/domains', {
      params: {
        limit: 10000,
        fields: 'parent',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch domains:', error);
    throw error;
  }
};

export const buildDomainTree = (domains: DomainItem[]): DomainTreeNode[] => {
  const domainMap = new Map<string, DomainTreeNode>();
  const rootNodes: DomainTreeNode[] = [];

  domains.forEach((domain) => {
    const node: DomainTreeNode = {
      key: domain.id,
      value: domain.id,
      title: domain.displayName || domain.name,
      children: [],
      description: domain.description,
      data: domain,
    };
    domainMap.set(domain.id, node);
  });

  domains.forEach((domain) => {
    const node = domainMap.get(domain.id);
    if (!node) return;

    if (domain.parent) {
      const parentNode = domainMap.get(domain.parent.id);
      if (parentNode) {
        if (!parentNode.children) {
          parentNode.children = [];
        }
        parentNode.children.push(node);
      } else {
        rootNodes.push(node);
      }
    } else {
      rootNodes.push(node);
    }
  });

  return rootNodes;
};

type DomainPatchOperation = {
  op: 'add' | 'remove' | 'replace';
  path: string;
  value?: unknown;
};

type DomainField = 'id' | 'name' | 'fullyQualifiedName' | 'description' | 'displayName';

const DOMAIN_FIELDS_MAP: Array<{ key: DomainField; path: string }> = [
  { key: 'id', path: '/domains/0/id' },
  { key: 'name', path: '/domains/0/name' },
  { key: 'fullyQualifiedName', path: '/domains/0/fullyQualifiedName' },
  { key: 'description', path: '/domains/0/description' },
  { key: 'displayName', path: '/domains/0/displayName' },
] as const;

const generateDomainPatchOperations = (
  selectedDomainId: string | null,
  domains: DomainItem[],
  currentDomains: Array<{
    id: string;
    type: string;
    name: string;
    fullyQualifiedName: string;
    description?: string;
    displayName?: string;
    inherited?: boolean;
  }>
): DomainPatchOperation[] => {
  const operations: DomainPatchOperation[] = [];

  if (selectedDomainId) {
    const selectedDomain = domains.find((d) => d.id === selectedDomainId);
    if (!selectedDomain) {
      throw new Error('선택된 도메인을 찾을 수 없습니다.');
    }

    if (currentDomains && currentDomains.length > 0) {
      const currentDomain = currentDomains[0];

      DOMAIN_FIELDS_MAP.forEach(({ key, path }) => {
        if (currentDomain[key] !== selectedDomain[key]) {
          operations.push({
            op: 'replace',
            path,
            value: selectedDomain[key],
          });
        }
      });

      operations.unshift({
        op: 'remove',
        path: '/domains/0/href',
      });

      if (currentDomain.inherited !== undefined) {
        operations.unshift({
          op: 'remove',
          path: '/domains/0/inherited',
        });
      }
    } else {
      operations.push({
        op: 'add',
        path: '/domains',
        value: [
          {
            id: selectedDomain.id,
            type: 'domain',
            name: selectedDomain.name,
            fullyQualifiedName: selectedDomain.fullyQualifiedName,
            description: selectedDomain.description,
            displayName: selectedDomain.displayName,
          },
        ],
      });
    }
  } else {
    if (currentDomains && currentDomains.length > 0) {
      operations.push({
        op: 'remove',
        path: '/domains/0',
      });
    }
  }

  return operations;
};

export const updateAssetDomains = async (
  entityType: string,
  serviceType: string | undefined,
  assetId: string,
  selectedDomainId: string | null,
  domains: DomainItem[],
  currentDomains: Array<{
    id: string;
    type: string;
    name: string;
    fullyQualifiedName: string;
    description?: string;
    displayName?: string;
    inherited?: boolean;
  }>
): Promise<void> => {
  try {
    const operations = generateDomainPatchOperations(selectedDomainId, domains, currentDomains);

    if (operations.length > 0) {
      const endpoint = getEntityEndpoint(entityType);
      const url = serviceType ? `${endpoint}/${serviceType}s/${assetId}` : `/${endpoint}/${assetId}`;
      await openMetadataApiClient.patch(url, operations, {
        headers: {
          'Content-Type': 'application/json-patch+json',
        },
      });
    }
  } catch (error) {
    console.error('Failed to update asset domains:', error);
    throw error;
  }
};
